import * as THREE from 'three';
import { IPartsCatalogSceneObject } from "../../interfaces/PartsCatalog";
import  Scenario  from "../scenarioElements/Scenario";
import { Timeline } from "../timeline/Timeline";
import Engine from "../Engine";
import { CustomMaterials } from "../CustomMaterials";
import store, { StoreActions } from '../../state/store';
import MaterialAsset from '../MaterialAsset';
import VisibilityManager from '../VisibilityManager';
import { VisibilityModes } from '../../interfaces/enums';

type TimedHighlightDescriptor = {
	object: THREE.Mesh;
	timeoutId: number;
};

const enum PartsCatalogObjectOverrideMode {
	Isolated, 
	Highlighted,
	Hidden,
	None,
}

type storedCameraTransform = {
	position: THREE.Vector3;
	rotation: THREE.Quaternion;
};

export default class PartsCatalogUtils
{
	private static _partsCatalogOpen = false;
	public static isPartsCatalogOpen(): boolean
	{
		return PartsCatalogUtils._partsCatalogOpen;
	}
	
	private static flattenedParts: Map<string, Map<string, IPartsCatalogSceneObject>> = new Map();
	private static hiddenNonPartsCatalogObjects: THREE.Object3D[] = [];
	private static partsCatalogObjectMap = new Map<string, THREE.Object3D>();
	private static timedHighlightDescriptor : TimedHighlightDescriptor | null = null;
	private static showGhostedUnselected = false;
	private static isFirstCatalogOpenForStep = new Map<string, boolean>();
	private static storedCameraTransform : storedCameraTransform | null = null;
	private static hiddenModelRoots: THREE.Object3D[] = []; 

	private static hideObject(object: THREE.Object3D)
	{
		const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(object.userData.guid)!;
		if(materialAsset)
		{
			(object as THREE.Mesh).material = CustomMaterials.getHiddenMaterial();
		}
		else if(object.userData.isModelRoot && !PartsCatalogUtils.hiddenModelRoots.includes(object))
		{
			PartsCatalogUtils.hiddenModelRoots.push(object);
		}
	}

	private static restoreObjectSolidVisibility(object: THREE.Object3D)
	{
		const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(object.userData.guid)!;
		if(materialAsset)
		{
			(object as THREE.Mesh).material = materialAsset.internalMaterial!;
		}
		else if(object.userData.isModelRoot)
		{
			const index = PartsCatalogUtils.hiddenModelRoots.indexOf(object);
			if(index !== -1) PartsCatalogUtils.hiddenModelRoots.splice(index, 1);
		}
	}

	private static ghostObject(object: THREE.Object3D)
	{
		const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(object.userData.guid);
		if(materialAsset)
		{
			(object as THREE.Mesh).material = CustomMaterials.getPartsCatalogGhostedMaterial();
		}
	}

	static GetFlattenedPart(stepId: string, partId: string): IPartsCatalogSceneObject | null
	{
		const map = PartsCatalogUtils.flattenedParts.get(stepId) || new Map();
		return map.get(partId) || null;
	}

	static AddObjectsToFlattenedMap(stepId: string, parts: IPartsCatalogSceneObject[])
	{
		const map = PartsCatalogUtils.flattenedParts.get(stepId) || new Map();
		parts.forEach(part => map.set(part.id, part));
		PartsCatalogUtils.flattenedParts.set(stepId, map);
	}

	static RemoveObjectsFromFlattenedMap(stepId: string, parts: IPartsCatalogSceneObject[])
	{
		const map = PartsCatalogUtils.flattenedParts.get(stepId) || new Map();
		parts.forEach(part => map.delete(part.id));
		PartsCatalogUtils.flattenedParts.set(stepId, map);
	}

	static GetPartsCatalogObject(objectGuid: string) : THREE.Object3D | null
	{
		return PartsCatalogUtils.partsCatalogObjectMap.get(objectGuid) || null;
	}

	static CatalogOpened(stepId: string)
	{
		const step = Scenario.getStep(stepId);
		const timeline = step?.timeline as Timeline;
		if(timeline) timeline.resetAnimations();

		const hiddenObjects = store.getState().partsCatalogModule.simulationHiddenObjects[Scenario.currentStep()?.id || ""] ?? [];
		const sceneRoot = Engine.getInstance().getSelectionRoot(step?.sceneId) as THREE.Object3D;

		const firstOpen = !PartsCatalogUtils.isFirstCatalogOpenForStep.get(stepId);
		PartsCatalogUtils.isFirstCatalogOpenForStep.set(stepId, true);

		sceneRoot.traverse((child) => 
		{
			if(PartsCatalogUtils.GetFlattenedPart(stepId, child.userData.guid))
			{
				PartsCatalogUtils.partsCatalogObjectMap.set(child.userData.guid, child);

				if(firstOpen && !hiddenObjects.includes(child.userData.guid) &&
				child.userData.visibility && child.userData.visibility[VisibilityManager.getRenderMode()] === VisibilityModes.Hidden)
				{
					store.dispatch(StoreActions.partsCatalog.addSimulationHiddenObject({stepId: stepId, partId: child.userData.guid}));
				}
			}
			else 
			{
				const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(child.userData.guid);
				if(materialAsset)
				{
					(child as THREE.Mesh).material = CustomMaterials.getHiddenMaterial();
					PartsCatalogUtils.hiddenNonPartsCatalogObjects.push(child);
				}
			}
		});

		PartsCatalogUtils._partsCatalogOpen = true;
		PartsCatalogUtils.buildCatalogObjectsVisibility();
	}

	static CatalogClosed(stepId?: string)
	{
		if(!stepId || !PartsCatalogUtils._partsCatalogOpen) return;

		const step = Scenario.getStep(stepId);
		const timeline = step?.timeline as Timeline;
		if(timeline) timeline.update(0);
		
		for(const object of PartsCatalogUtils.hiddenNonPartsCatalogObjects)
		{
			const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(object.userData.guid)!;
			if(materialAsset)
			{
				(object as THREE.Mesh).material = materialAsset.internalMaterial!;
			}
		}

		PartsCatalogUtils.partsCatalogObjectMap.clear();
		PartsCatalogUtils.hiddenNonPartsCatalogObjects.length = 0;
		PartsCatalogUtils.hiddenModelRoots.length = 0;
		PartsCatalogUtils._partsCatalogOpen = false;
		PartsCatalogUtils.storedCameraTransform = null;
		
		if (PartsCatalogUtils.timedHighlightDescriptor !== null) 
		{
			clearTimeout(PartsCatalogUtils.timedHighlightDescriptor.timeoutId);
			store.dispatch(StoreActions.partsCatalog.setHighlightedPartId(''));
		}

		Engine.getInstance().getInteractiveModeManager().onSimulationStarted(); //restores interacive camera view, if one exists in the step
		const sceneRoot = Engine.getInstance().getSelectionRoot(step?.sceneId) as THREE.Object3D;
		VisibilityManager.setObjectsVisibilityMode([sceneRoot], [sceneRoot.userData.visibility[VisibilityManager.getRenderMode()]]);
	}

	static OnSimulationStopped()
	{
		if(PartsCatalogUtils._partsCatalogOpen)
		{
			PartsCatalogUtils.CatalogClosed(Scenario.currentStep()?.id);
		}

		PartsCatalogUtils.flattenedParts.clear();
		PartsCatalogUtils.partsCatalogObjectMap.clear();
		PartsCatalogUtils.hiddenNonPartsCatalogObjects = [];
		PartsCatalogUtils.isFirstCatalogOpenForStep.clear();
	}

	static ShowGhostedUnselected(value: boolean)
	{
		PartsCatalogUtils.showGhostedUnselected = value;
	}

	static buildCatalogObjectsVisibility()
	{
		const currentStepId = Scenario.currentStep()?.id || "";

		const partsCatalogObjects = PartsCatalogUtils.flattenedParts.get(currentStepId)!;
		const isolatedObjects = store.getState().partsCatalogModule.simulationIsolatedObjects[currentStepId] ?? [];
		const highlightedObjects = store.getState().partsCatalogModule.simulationHighlightedObjects[currentStepId] ?? [];
		const hiddenObjects = store.getState().partsCatalogModule.simulationHiddenObjects[currentStepId] ?? [];

		const objectsToFocus: THREE.Object3D[] = [];
		const determineObjectVisibilityRecursive = (partsCatalogObject: IPartsCatalogSceneObject, overrideMode: PartsCatalogObjectOverrideMode) =>
		{
			const object = PartsCatalogUtils.GetPartsCatalogObject(partsCatalogObject.id);
			if(!object) return;

			if(isolatedObjects.includes(partsCatalogObject.id))
			{
				PartsCatalogUtils.restoreObjectSolidVisibility(object);
				overrideMode = PartsCatalogObjectOverrideMode.Isolated;
				objectsToFocus.push(object);
			}
			else if(highlightedObjects.includes(partsCatalogObject.id))
			{
				PartsCatalogUtils.restoreObjectSolidVisibility(object);
				overrideMode = PartsCatalogObjectOverrideMode.Highlighted;
			}
			else if(hiddenObjects.includes(partsCatalogObject.id))
			{
				PartsCatalogUtils.hideObject(object);
				overrideMode = PartsCatalogObjectOverrideMode.Hidden;
			}
			else
			{
				if(overrideMode !== PartsCatalogObjectOverrideMode.None)
				{
					switch(overrideMode)
					{
						case PartsCatalogObjectOverrideMode.Isolated:
							PartsCatalogUtils.restoreObjectSolidVisibility(object);
							break;
						case PartsCatalogObjectOverrideMode.Highlighted:
							PartsCatalogUtils.restoreObjectSolidVisibility(object);
							break;
						case PartsCatalogObjectOverrideMode.Hidden:
							PartsCatalogUtils.hideObject(object);
							break;
					}
				}
				else
				{
					if(isolatedObjects.length)
					{
						PartsCatalogUtils.hideObject(object);
					}
					else if(highlightedObjects.length)
					{
						PartsCatalogUtils.ghostObject(object);
					}
					else 
					{
						PartsCatalogUtils.restoreObjectSolidVisibility(object);
					}
				}
			}

			if(overrideMode === PartsCatalogObjectOverrideMode.Isolated)
			{
				objectsToFocus.push(object);
			}

			for(const child of partsCatalogObject.children)
			{
				determineObjectVisibilityRecursive(child, overrideMode);
			}
		}

		for(const partsCatalogObject of partsCatalogObjects.values())
		{
			if(partsCatalogObject.parent) continue; //we only want to traverse from root catalog nodes
			determineObjectVisibilityRecursive(partsCatalogObject, PartsCatalogObjectOverrideMode.None);
		}

		const cameraController = Engine.getInstance().getCameraController();
		const camera = cameraController?.camera;
		if(isolatedObjects.length === 0)
		{
			if(PartsCatalogUtils.storedCameraTransform)
			{
				cameraController?.setTransformAnimation(PartsCatalogUtils.storedCameraTransform.position, PartsCatalogUtils.storedCameraTransform.rotation, false);
				const cameraForward = new THREE.Vector3();
				camera?.getWorldDirection(cameraForward);
				cameraController?.getAnchor()?.addVectors(PartsCatalogUtils.storedCameraTransform.position, cameraForward);
				PartsCatalogUtils.storedCameraTransform = null;
			}
		}
		else
		{
			if(!PartsCatalogUtils.storedCameraTransform)
			{
				if ( camera )
				{
					PartsCatalogUtils.storedCameraTransform = {
						position: camera?.position.clone(),
						rotation: camera?.quaternion.clone(),
					};
				}
			}
		}
	
		if(objectsToFocus.length)
		{
			cameraController?.focus(objectsToFocus);
		}
	}

	static PartFocus(objectId: string | null, animateCamera = true)
	{
		if(objectId)
		{
			const object = PartsCatalogUtils.GetPartsCatalogObject(objectId);
			if(!object) return;

			if(!PartsCatalogUtils.storedCameraTransform)
			{
				const cameraController = Engine.getInstance().getCameraController();
				if (cameraController?.camera)
				{
					PartsCatalogUtils.storedCameraTransform = {
						position: cameraController.camera.position.clone(),
						rotation: cameraController.camera.quaternion.clone(),
					};
				}
			}

			const objectsToFocus: THREE.Object3D[] = [];
			const determineObjectVisibilityRecursive = (partsCatalogObject: IPartsCatalogSceneObject, overrideMode: PartsCatalogObjectOverrideMode) =>
			{
				const object = PartsCatalogUtils.GetPartsCatalogObject(partsCatalogObject.id);
				if(!object) return;

				if(partsCatalogObject.id === objectId)
				{
					overrideMode = PartsCatalogObjectOverrideMode.Isolated;
				}

				if(overrideMode === PartsCatalogObjectOverrideMode.Isolated)
				{
					PartsCatalogUtils.restoreObjectSolidVisibility(object);
					objectsToFocus.push(object);
				}
				else if(PartsCatalogUtils.showGhostedUnselected)
				{
					PartsCatalogUtils.ghostObject(object);
				}
				else
				{
					PartsCatalogUtils.hideObject(object);
				}

				for(const child of partsCatalogObject.children)
				{
					determineObjectVisibilityRecursive(child, overrideMode);
				}
			};

			const partsCatalogObjects = PartsCatalogUtils.flattenedParts.get(Scenario.currentStep()!.id)!;
			for(const partsCatalogObject of partsCatalogObjects.values())
			{
				if(partsCatalogObject.parent) continue; //we only want to traverse from root catalog nodes
				determineObjectVisibilityRecursive(partsCatalogObject, PartsCatalogObjectOverrideMode.None);
			}

			if(objectsToFocus.length && animateCamera)
			{
				Engine.getInstance().getCameraController()?.focus(objectsToFocus);
			}
		}
		else
		{
			PartsCatalogUtils.buildCatalogObjectsVisibility();
		}
	}
	
	static onObjectClicked(object: THREE.Mesh) : boolean
	{
		const material = object.material as THREE.Material;
		if(!material || (!material.visible && !object.userData.isModelRoot) ||
			(object.userData.isModelRoot && PartsCatalogUtils.hiddenModelRoots.includes(object))) return false;

		if (PartsCatalogUtils.timedHighlightDescriptor !== null) 
		{
			clearTimeout(PartsCatalogUtils.timedHighlightDescriptor.timeoutId);
			store.dispatch(StoreActions.partsCatalog.setHighlightedPartId(''));
		}

		store.dispatch(StoreActions.partsCatalog.setHighlightedPartId(object.userData.guid));
		
		PartsCatalogUtils.timedHighlightDescriptor = { 
			object: object, 
			timeoutId: setTimeout(() => 
			{
				store.dispatch(StoreActions.partsCatalog.setHighlightedPartId(''));
				PartsCatalogUtils.timedHighlightDescriptor = null;
			}, 3000) as any as number,
		};

		return true;
	}
}